/**
 * 车辆框绘制（DrawVehicle）
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 根据车辆中心点 + 朝向 + 尺寸，计算车框矩形顶点（米级偏移 → 经纬度）
 * 2. 通过 BMapGL 要素图层（FillLayer / LineLayer）渲染车框、朝向线、基准线
 * 3. 提供清除入口（clearRectangle）
 *
 * 为什么用要素图层而不是原生 Polygon / Polyline 覆盖物：
 * 车框是米级小图形，车号搜索定位后在大比例尺下展示，原生覆盖物在缩放动画期间
 * 会因 WebGL float 精度不足而抖动（百度官方已知问题，见 JSAPI GL 更新日志
 * 2023.07.13）。要素图层支持渲染参考中心点（referCenter，顶点相对参考中心渲染
 * 以恢复精度），这里通过 setData 的 changeCenter 参数自动取首个要素坐标。
 *
 * 坐标系：适配器在初始化时设置了全局 BMapGL.coordType = 'BMAP_COORD_GCJ02'
 * （BaiduMapAdapter），原生覆盖物全部按 GCJ02 转换。但该全局设置不会传递到
 * FeatureLayer 系图层（SDK 的 getBaseOptions 只识别数值 2/5），
 * 所以 FillLayer / LineLayer 必须显式传 crs: 'GCJ02'。
 *
 * 不知道业务存在，不知道 React 存在
 */
export class DrawVehicle {
  mapEngineRef: any;
  lon: number;
  lat: number;
  /** 车辆朝向，单位：弧度（约定：0 = 正东，逆时针增加，与后端 radian 字段一致） */
  heading: number;
  /** 已挂到地图上的要素图层（车框 FillLayer + 朝向线/基准线 LineLayer） */
  private featureLayers: any[] = [];
  /** 朝向线端点的圆点 Marker */
  private dotOverlay: any = null;

  constructor({
    lon,
    lat,
    heading,
    mapEngineRef,
  }: {
    lon: any;
    lat: any;
    heading: any;
    mapEngineRef: any;
  }) {
    this.mapEngineRef = mapEngineRef;
    this.lon = Number(lon);
    this.lat = Number(lat);
    this.heading = Number(heading);
  }

  private getMetersPerDegree(lat: number) {
    const latRad = (lat * Math.PI) / 180;
    const metersPerDegLat =
      111132.92 -
      559.82 * Math.cos(2 * latRad) +
      1.175 * Math.cos(4 * latRad);
    const metersPerDegLon =
      111412.84 * Math.cos(latRad) - 93.5 * Math.cos(3 * latRad);
    return { metersPerDegLat, metersPerDegLon };
  }

  /**
   * 从中心点沿指定朝向偏移 distanceMeters 米，返回 [lon, lat]
   * @param rad 朝向角，单位：弧度（约定：0 = 正东，逆时针增加）
   */
  private offsetFromCenter(
    lon: number,
    lat: number,
    rad: number,
    distanceMeters: number
  ): [number, number] {
    const { metersPerDegLat, metersPerDegLon } = this.getMetersPerDegree(lat);
    const dEast = distanceMeters * Math.cos(rad);
    const dNorth = distanceMeters * Math.sin(rad);
    return [lon + dEast / metersPerDegLon, lat + dNorth / metersPerDegLat];
  }

  /**
   * 图层上图并写入 GeoJSON 数据。
   * changeCenter：以首个要素的首个坐标作为渲染参考中心（referCenter），
   * 解决大比例尺下米级小面/线的缩放抖动。
   */
  private addFeatureLayer(layer: any, features: any[]) {
    const map = this.mapEngineRef.map;
    if (!map?.addNormalLayer) return;
    map.addNormalLayer(layer);
    layer.setData(
      { type: 'FeatureCollection', features },
      { changeCenter: true }
    );
    this.featureLayers.push(layer);
  }

  private createPolygonLayer(type: 'inner' | 'outline') {
    return new window.BMapGL.FillLayer({
      // 全局 coordType 不会传递到 FeatureLayer 系图层，必须显式声明数据源坐标系
      crs: 'GCJ02',
      // NormalLayer 默认 maxZoom=21，地图 maxZoom=25，超过 21 图层会被自动隐藏
      maxZoom: 25,
      border: true,
      style: {
        fillColor: 'rgba(126,103,230,0.4)',
        fillOpacity: 0.4,
        strokeColor: '#7E67E6',
        strokeWeight: 2,
        strokeStyle: type === 'inner' ? 'solid' : 'dashed',
      },
    });
  }

  private createLineLayer(type: 'dash' | 'solid') {
    return new window.BMapGL.LineLayer({
      // 同 FillLayer：显式声明数据源为 GCJ02（全局 coordType 不传递到 FeatureLayer 系图层）
      crs: 'GCJ02',
      // 同 FillLayer：对齐地图 maxZoom=25，避免超过 21 被自动隐藏
      maxZoom: 25,
      style: {
        strokeColor: 'red',
        strokeWeight: 4,
        strokeStyle: type === 'dash' ? 'dashed' : 'solid',
        dashArray: [4, 4, 4, 4],
      },
    });
  }

  private createLineGeoJSON(id: string, points: [number, number][]) {
    return {
      type: 'Feature',
      properties: { id },
      geometry: { type: 'LineString', coordinates: points },
    };
  }

  drawDirectionLine(lineLength = 3) {
    const start: [number, number] = [this.lon, this.lat];
    const end = this.offsetFromCenter(this.lon, this.lat, this.heading, lineLength);

    this.addFeatureLayer(this.createLineLayer('dash'), [
      this.createLineGeoJSON('directionLine', [start, end]),
    ]);

    // 在朝向线远离车框的端点加一个红色圆点
    const dotIcon =
      'data:image/svg+xml;base64,' +
      window.btoa(
        '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10"><circle cx="5" cy="5" r="4" fill="red"/></svg>'
      );
    const dotFeature = this.mapEngineRef.createPointFeature(
      end,
      dotIcon,
      'headingDot',
      { imageSize: { width: 10, height: 10 } },
      [0.5, 0.5]
    );
    this.dotOverlay = dotFeature.overlay;
    this.mapEngineRef.map?.addOverlay?.(this.dotOverlay);
  }

  drawBaseLine(lineLength = 3) {
    const start: [number, number] = [this.lon, this.lat];
    // 基准线 = 0° 参考方向（正东）
    const end = this.offsetFromCenter(this.lon, this.lat, 0, lineLength);
    this.addFeatureLayer(this.createLineLayer('solid'), [
      this.createLineGeoJSON('baseline', [start, end]),
    ]);
  }

  /**
   * @param radian 车辆朝向，单位：弧度（0 = 正东，逆时针增加）
   */
  drawDirectionRectangle(
    lon: number,
    lat: number,
    radian: number,
    vehicleTypeData: any,
    type: 'inner' | 'outline'
  ) {
    const width = Number(vehicleTypeData?.width ?? 0);
    const frontEdgeToCenter = Number(vehicleTypeData?.frontEdgeToCenter ?? 0);
    const backEdgeToCenter = Number(vehicleTypeData?.backEdgeToCenter ?? 0);
    const longLen = Math.sqrt(
      Math.pow(width / 2, 2) + Math.pow(frontEdgeToCenter, 2)
    );
    const shortLen = Math.sqrt(
      Math.pow(width / 2, 2) + Math.pow(backEdgeToCenter, 2)
    );
    // atan 直接返回弧度，无需转换
    const longAtan = Math.atan(width / 2 / frontEdgeToCenter);
    const shortAtan = Math.atan(width / 2 / backEdgeToCenter);
    // 从车尾左侧起顺时针，依次为 P1、P2、P3、P4
    const angleP1 = Math.PI + radian + shortAtan;
    const angleP2 = Math.PI + radian - shortAtan;
    const angleP3 = radian + longAtan;
    const angleP4 = radian - longAtan;

    const P1 = this.offsetFromCenter(lon, lat, angleP1, shortLen);
    const P2 = this.offsetFromCenter(lon, lat, angleP2, shortLen);
    const P3 = this.offsetFromCenter(lon, lat, angleP3, longLen);
    const P4 = this.offsetFromCenter(lon, lat, angleP4, longLen);

    const coordinates = [[P1, P2, P3, P4, P1]];
    this.drawRectangle(coordinates, type);
  }

  drawRectangle(coordinates: any[], type: 'inner' | 'outline') {
    const ring = coordinates[0];
    this.addFeatureLayer(this.createPolygonLayer(type), [
      {
        type: 'Feature',
        properties: { id: 'vehicleRect' },
        // GeoJSON 标准要求环首尾闭合，保留 P1 收尾
        geometry: { type: 'Polygon', coordinates: [ring] },
      },
    ]);
  }

  clearRectangle() {
    const map = this.mapEngineRef.map;
    this.featureLayers.forEach((layer) => map?.removeNormalLayer?.(layer));
    this.featureLayers = [];
    if (this.dotOverlay) {
      map?.removeOverlay?.(this.dotOverlay);
      this.dotOverlay = null;
    }
  }
}
